import  { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AuthCard from "../../components/layout/AuthCard";
import Input from "../../components/ui/Input";
import OtpInput from "../../components/ui/OtpInput";
import PasswordInput from "../../components/ui/PasswordInput";
import Button from "../../components/ui/Button";
import { sendOtp, forgotPasswordSubmit } from "../../services/api";
import { getPasswordError } from "../../utils/password";

const ErrorBanner = ({ message }) =>
  message ? (
    <div className="flex items-center gap-2 bg-statusRedBg border border-red-200 text-statusRed rounded-lg px-4 py-3 mb-5 text-sm">
      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      {message}
    </div>
  ) : null;

// ─── Forgot Password ──────────────────────────────────────────────────────────
// Step 1: Enter email → send OTP
// Step 2: Enter OTP + new password → reset
const ForgotPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(location.state?.email || "");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [hashId, setHashId] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [errors, setErrors] = useState({});
  const [bannerError, setBannerError] = useState("");
  const [done, setDone] = useState(false);

  // ── Step 1 ─────────────────────────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!email.trim()) errs.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = "Enter a valid email address.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setBannerError("");
    setLoading(true);
    try {
      const res = await sendOtp({ email, emailType: "otp" });
      const id = res?.data?.hashId || res?.data?.hashID || res?.hashId || res?.hashID;
      if (!id) throw new Error("Could not get verification token. Please try again.");
      setHashId(id);
      setStep(2);
      toast.success("OTP sent to your email.");
    } catch (err) {
      setBannerError(err?.response?.data?.message || err?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const handleResend = async () => {
    setBannerError("");
    setResending(true);
    try {
      const res = await sendOtp({ email, emailType: "otp" });
      const id = res?.data?.hashId || res?.data?.hashID || res?.hashId || res?.hashID;
      if (!id) throw new Error("Could not resend OTP.");
      setHashId(id);
      setOtp("");
      toast.success("OTP resent to your email.");
    } catch (err) {
      setBannerError(err?.response?.data?.message || err?.message || "Failed to resend OTP.");
    } finally {
      setResending(false);
    }
  };

  // ── Step 2 ─────────────────────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!otp.trim() || otp.length < 4) errs.otp = "Enter the OTP sent to your email.";
    const passwordError = getPasswordError(newPassword);
    if (passwordError) errs.newPassword = passwordError;
    if (!confirmPassword) errs.confirmPassword = "Please confirm your password.";
    else if (newPassword !== confirmPassword) errs.confirmPassword = "Passwords do not match.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setBannerError("");
    setLoading(true);
    try {
      await forgotPasswordSubmit({ newPassword, username: email, hashId, otp });
      setDone(true);
    } catch (err) {
      setBannerError(err?.response?.data?.message || err?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Success ────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <AuthCard>
        <div className="text-center">
          <div className="w-12 h-12 bg-statusGreenBg rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-statusGreen" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-textPrimary mb-2">Password reset!</h1>
          <p className="text-sm text-textMuted mb-6">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <button
            onClick={() => navigate("/auth/login", { state: { email } })}
            className="w-full bg-brand text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-brandHover transition-colors"
          >
            Back to Sign in
          </button>
        </div>
      </AuthCard>
    );
  }

  // ── Step 1: Email ──────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <AuthCard>
        <h1 className="text-xl font-semibold text-textPrimary mb-1">Forgot password?</h1>
        <p className="text-sm text-textMuted mb-6">
          Enter your email and we'll send you a One-time passcode.
        </p>
        <ErrorBanner message={bannerError} />
        <form onSubmit={handleSendOtp} noValidate>
          <Input
            label="Email address"
            type="email"
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors({}); setBannerError(""); }}
            placeholder="you@company.com"
            error={errors.email}
            required
          />
          <div className="mt-6 space-y-3">
            <Button type="submit" loading={loading}>Send OTP</Button>
            <div className="text-center">
              <Link to="/auth/login" className="text-sm text-brand hover:underline">
                Back to Sign in
              </Link>
            </div>
          </div>
        </form>
      </AuthCard>
    );
  }

  // ── Step 2: OTP + New password ─────────────────────────────────────────────
  return (
    <AuthCard>
      <h1 className="text-xl font-semibold text-textPrimary mb-1">Reset your password</h1>
      <p className="text-sm text-textMuted mb-6">
        We sent a code to <span className="font-medium text-textPrimary">{email}</span>.
        Enter it below along with your new password.
      </p>
      <ErrorBanner message={bannerError} />
      <form onSubmit={handleReset} noValidate>
        <div className="space-y-4">
          {/* OTP */}
          <div>
            <OtpInput
              value={otp}
              onChange={(value) => { setOtp(value); setErrors((p) => ({ ...p, otp: "" })); }}
              error={errors.otp}
            />
            <div className="mt-1 text-right">
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-xs text-brand hover:underline disabled:opacity-50"
              >
                {resending ? "Sending…" : "Resend code"}
              </button>
            </div>
          </div>

          {/* New password */}
          <PasswordInput
            label="New password"
            name="newPassword"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setErrors((p) => ({ ...p, newPassword: "" })); }}
            placeholder="Enter new password"
            error={errors.newPassword}
            required
            showRequirements
          />

          {/* Confirm password */}
          <PasswordInput
            label="Confirm new password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setErrors((p) => ({ ...p, confirmPassword: "" })); }}
            placeholder="Re-enter new password"
            error={errors.confirmPassword}
            required
          />
        </div>

        <div className="mt-6 space-y-3">
          <Button type="submit" loading={loading}>Reset Password</Button>
          <button
            type="button"
            onClick={() => { setStep(1); setBannerError(""); setErrors({}); }}
            className="w-full text-sm text-textMuted hover:text-textPrimary text-center transition-colors"
          >
            ← Use a different email
          </button>
        </div>
      </form>
    </AuthCard>
  );
};

export default ForgotPassword;
